// src/platform/push.ts
// Unified push registration: Web Push (web) vs FCM/APNs (native)

import { isNative, isIos } from './platform';
import { notificationsApi } from '../api/notifications/notifications';

/** Register for push notifications on the current platform. Returns true on success. */
export async function registerPush(): Promise<boolean> {
  console.log('[Push] registerPush() called, isNative:', isNative());
  if (isNative()) {
    return registerNativePush();
  }
  return registerWebPush();
}

/** Request push permission on the current platform. Returns 'granted' | 'denied' | 'default'. */
export async function requestPushPermission(): Promise<'granted' | 'denied' | 'default'> {
  if (isNative()) {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const result = await PushNotifications.requestPermissions();
    return result.receive === 'granted' ? 'granted' : 'denied';
  }
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.requestPermission();
}

/** Check if push can be requested (not permanently denied). */
export function canRequestPush(): boolean {
  if (isNative()) return true;
  if (typeof Notification === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  return Notification.permission !== 'denied';
}

/** Check current permission status. */
export async function getPushPermissionStatus(): Promise<'granted' | 'denied' | 'default'> {
  if (isNative()) {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const status = await PushNotifications.checkPermissions();
    const mapped = status.receive === 'granted' ? 'granted' : status.receive === 'denied' ? 'denied' : 'default';
    console.log('[Push] getPushPermissionStatus native:', status.receive, '→', mapped);
    return mapped;
  }
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission as 'granted' | 'denied' | 'default';
}

/** Unsubscribe from push. */
export async function unregisterPush(): Promise<void> {
  if (isNative()) {
    return;
  }
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await subscription.unsubscribe();
  } catch {
    // ignore
  }
}

// ── FCM token cache ──

let cachedNativeToken: string | null = null;

/** Get the cached native push token for the current device */
export function getCachedNativeToken(): string | null {
  return cachedNativeToken;
}

/**
 * Start listening for push registration events early.
 * On iOS: triggers PushNotifications.register() so Firebase can exchange
 * the APNs token for an FCM token (saved to UserDefaults by AppDelegate).
 * On Android: Capacitor gives the FCM token directly via the registration event.
 */
export async function cacheNativeToken(): Promise<void> {
  console.log('[Push] cacheNativeToken() called, isNative:', isNative());
  if (!isNative()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Android: Capacitor gives the FCM token directly
    await PushNotifications.addListener('registration', (token) => {
      console.log('[Push] ★ Capacitor registration event:', token.value?.substring(0, 30) + '…');
      // On Android this is the FCM token. On iOS this is the APNs token (we ignore it).
      if (!isIos()) {
        cachedNativeToken = token.value;
      }
    });
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('[Push] ★ registration ERROR:', JSON.stringify(err));
    });

    // If permission is already granted, trigger register() early
    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'granted') {
      console.log('[Push] permission already granted, calling register() early');
      await PushNotifications.register();

      // iOS: read FCM token from UserDefaults (written by AppDelegate)
      if (isIos()) {
        console.log('[Push] iOS: polling UserDefaults for FCM token…');
        const fcmToken = await pollForIOSFCMToken(10000);
        if (fcmToken) {
          cachedNativeToken = fcmToken;
          console.log('[Push] iOS FCM token cached early:', fcmToken.substring(0, 30) + '…');
        }
      }
    }
  } catch (err) {
    console.error('[Push] cacheNativeToken error:', err);
  }
}

/**
 * Poll UserDefaults for the FCM token that Firebase writes via AppDelegate.
 * Capacitor Preferences wraps UserDefaults on iOS.
 */
async function pollForIOSFCMToken(timeoutMs: number): Promise<string | null> {
  const { Preferences } = await import('@capacitor/preferences');
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const { value } = await Preferences.get({ key: 'fcm_push_token' });
    if (value) {
      console.log('[Push] ★ iOS FCM token from UserDefaults:', value.substring(0, 30) + '…');
      return value;
    }
    await new Promise(r => setTimeout(r, 500));
  }
  console.warn('[Push] iOS FCM token not found in UserDefaults after', timeoutMs, 'ms');
  return null;
}

// ── Private ──

async function registerNativePush(): Promise<boolean> {
  console.log('[Push] registerNativePush() START, cachedToken:', cachedNativeToken ? 'yes' : 'no');
  console.log('[Push] access_token in localStorage:', !!localStorage.getItem('access_token'));
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permResult = await PushNotifications.requestPermissions();
    console.log('[Push] native permission result:', JSON.stringify(permResult));
    if (permResult.receive !== 'granted') {
      console.warn('[Push] permission NOT granted, aborting');
      return false;
    }

    // If we already have a cached FCM token, send it
    if (cachedNativeToken) {
      console.log('[Push] using cached FCM token:', cachedNativeToken.substring(0, 30) + '…');
      return sendNativeToken(cachedNativeToken);
    }

    // Trigger APNs/FCM registration
    console.log('[Push] no cached token, calling PushNotifications.register()…');
    PushNotifications.register();

    if (isIos()) {
      // iOS: poll UserDefaults for the FCM token written by AppDelegate
      const fcmToken = await pollForIOSFCMToken(15000);
      if (fcmToken) {
        cachedNativeToken = fcmToken;
        return sendNativeToken(fcmToken);
      }
      console.warn('[Push] ⚠ iOS FCM token not available after 15s');
      return false;
    }

    // Android: wait for Capacitor registration event (gives FCM token directly)
    return new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('[Push] ⚠ Android FCM registration timed out after 15s');
        resolve(false);
      }, 15_000);

      PushNotifications.addListener('registration', async (token) => {
        clearTimeout(timeout);
        console.log('[Push] ★ Android FCM token:', token.value?.substring(0, 30) + '…');
        cachedNativeToken = token.value;
        resolve(await sendNativeToken(token.value));
      });

      PushNotifications.addListener('registrationError', (err) => {
        clearTimeout(timeout);
        console.error('[Push] ★ registrationError:', JSON.stringify(err));
        resolve(false);
      });
    });
  } catch (err) {
    console.error('[Push] registerNativePush EXCEPTION:', err);
    return false;
  }
}

async function sendNativeToken(token: string): Promise<boolean> {
  const accessToken = localStorage.getItem('access_token');
  console.log('[Push] sendNativeToken() called');
  console.log('[Push]   token:', token.substring(0, 30) + '…');
  console.log('[Push]   access_token present:', !!accessToken);

  if (!accessToken) {
    console.error('[Push] ⚠ NO ACCESS TOKEN — skipping.');
    return false;
  }

  const platform = (await import('./platform')).getPlatform() as 'ios' | 'android';
  const payload = {
    subscription: {
      endpoint: token,
      keys: { p256dh: '', auth: '' },
      expirationTime: null,
    },
    platform,
  };

  try {
    console.log('[Push] attempting via axios…');
    const result = await notificationsApi.registerPushToken(payload);
    console.log('[Push] ✓ axios succeeded! Response:', JSON.stringify(result));
    return true;
  } catch (err: any) {
    console.error('[Push] ✗ axios failed:', err?.message, 'status:', err?.response?.status);
  }

  // Fallback: raw XMLHttpRequest
  try {
    const { API_BASE_URL } = await import('../env');
    const url = `${API_BASE_URL}/notifications/push-token`;
    console.log('[Push] fallback: raw XHR to', url);
    const result = await new Promise<boolean>((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      xhr.timeout = 15000;
      xhr.onload = () => {
        console.log('[Push] XHR response:', xhr.status, xhr.responseText);
        resolve(xhr.status >= 200 && xhr.status < 300);
      };
      xhr.onerror = () => { console.error('[Push] XHR error'); resolve(false); };
      xhr.ontimeout = () => { console.error('[Push] XHR timeout'); resolve(false); };
      xhr.send(JSON.stringify(payload));
    });
    if (result) {
      console.log('[Push] ✓ XHR fallback succeeded!');
      return true;
    }
  } catch (err: unknown) {
    console.error('[Push] XHR fallback error:', err instanceof Error ? err.message : err);
  }

  return false;
}

async function registerWebPush(): Promise<boolean> {
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (!vapidKey) return false;
  if (!canRequestPush()) return false;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const { urlBase64ToUint8Array } = await import('../utils/push-utils');
    const registration = await navigator.serviceWorker.register('/sw.js');
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys) return false;

    await notificationsApi.registerPushToken({
      subscription: {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh!, auth: json.keys.auth! },
        expirationTime: (json.expirationTime as number | null) ?? null,
      },
      platform: 'web',
    });
    return true;
  } catch {
    return false;
  }
}
